/**
 * An embedded Excel chart, rebuilt with Recharts and floated over a live Univer sheet
 * (sdmsLiveCharts.ts) — the SDMS Template Builder design sheet and SDMS Result Entry.
 *
 * Draws the frame even with no points yet (axes, value-axis title, legend), as Excel / the
 * Windows builder / ALPACA do: a new calibration has its Known Conc column but no readings, and
 * the chart must be there to watch the curve form as the readings are typed. Display only.
 */
import { useSyncExternalStore } from 'react';
import * as numfmtNs from 'numfmt';
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { niceAxis } from './sdmsChartAnchor';
import { chartData, subscribeChart } from './sdmsChartStore';

/** Registered as a Univer float-DOM component; Univer passes the layer's `data` = the chart id,
 *  and the plotted values come live from chartStore (published by the designer on every change). */
export function SdmsChartPreview({ data: layer }: { data?: { chartId?: string } }) {
  const id = layer?.chartId ?? '';
  const data = useSyncExternalStore(fn => subscribeChart(id, fn), () => chartData(id));
  if (!data) return null;
  const ys = data.points.map(p => p.y).filter((v): v is number => v != null);
  const xA = niceAxis(Math.max(0, ...data.points.map(p => p.x)));
  const yA = niceAxis(Math.max(0, ...ys), 7);
  const yFmt = (v: number): string => {
    const fmt = (numfmtNs as unknown as { format?: (p: string, v: number) => unknown }).format;
    if (data.yFormat && data.yFormat !== 'General' && typeof fmt === 'function') {
      try { return String(fmt(data.yFormat, v)); } catch { /* General below */ }
    }
    return String(Number(v.toFixed(10)));
  };
  return (
    <div title="Chart from the workbook (preview — the chart itself is edited in Excel or the Windows builder)"
      style={{ width: '100%', height: '100%', boxSizing: 'border-box', padding: '6px 8px', background: '#fff', border: '1px solid #c8c8c8', pointerEvents: 'none' }}>
      <ResponsiveContainer>
        <LineChart data={data.points} margin={{ top: 10, right: 14, bottom: 20, left: 4 }}>
          <CartesianGrid horizontal vertical={false} stroke="#d9d9d9" />
          <XAxis type="number" dataKey="x" domain={[0, xA.max]} ticks={xA.ticks} allowDataOverflow fontSize={10} tick={{ fill: '#555' }} />
          <YAxis domain={[0, yA.max]} ticks={yA.ticks} allowDataOverflow width={54} tickFormatter={yFmt} fontSize={10} tick={{ fill: '#555' }}
            label={data.yTitle ? { value: data.yTitle, angle: -90, position: 'insideLeft', fontSize: 10, fill: '#555', fontWeight: 700 } : undefined} />
          {data.legend && <Legend layout="vertical" align="right" verticalAlign="middle" />}
          <ReferenceLine y={yA.max} stroke="#666" strokeWidth={1} ifOverflow="visible" />
          <ReferenceLine x={xA.max} stroke="#666" strokeWidth={1} ifOverflow="visible" />
          <Line type="linear" dataKey="y" name="Series 1" stroke="#2f7fd1" strokeWidth={2} connectNulls={false}
            dot={data.markers ? { r: 3 } : false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
